import { isAuthorized, lazyLoadView, supportOnly, piloteAndAuditor } from '@/router/routing'
import MainContent from '@/components/layout/MainContent.vue'
import type { Route, RouteConfig } from 'vue-router'
import { CATALOG_ROUTES } from '@/contexts/administration/catalog/Catalog.constants'

export const catalogRoutes: RouteConfig = {
  path: 'catalog',
  name: CATALOG_ROUTES.CATALOG,
  component: MainContent,
  meta: {
    i18nTitle: 'administration.menu.catalog',
    icon: 'medkit',
    multi: true,
    dummy: true,
    requires_auth: true,
    requires_tenant: false,
    requires_role: piloteAndAuditor,
    is_authorized: (route: Route) => isAuthorized(route),
    doc_id: 'catalog',
    feature_id: 'CATALOG',
  },
  children: [
    {
      path: 'policies',
      name: CATALOG_ROUTES.POLICIES,
      component: () => lazyLoadView(import('@/contexts/administration/catalog/CatalogPoliciesView.vue')),
      meta: {
        i18nTitle: 'administration.menu.policies',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: false,
        requires_role: piloteAndAuditor,
        doc_id: 'catalog',
        feature_id: 'CATALOG',
      },
      children: [
        {
          path: ':id',
          name: CATALOG_ROUTES.POLICY_VERSION,
          component: () => lazyLoadView(import('@/contexts/policies/PolicyVersionView.vue')),
          meta: {
            title: null,
            requires_auth: true,
            requires_tenant: false,
            sidebar: false,
            is_authorized: (route: Route) => isAuthorized(route),
            requires_role: piloteAndAuditor,
            doc_id: 'catalog_policy_details',
          },
          children: [
            {
              path: ':policy_id/diff/:ref',
              name: CATALOG_ROUTES.POLICY_DIFFERENCES,
              component: () =>
                lazyLoadView(import('@/contexts/administration/catalog/CatalogPolicyDifferencesView.vue')),
              meta: {
                i18nTitle: 'administration.menu.differences',
                requires_auth: true,
                requires_tenant: false,
                sidebar: false,
                is_authorized: (route: Route) => isAuthorized(route),
                requires_role: piloteAndAuditor,
                doc_id: 'policy_diff',
              },
            },
          ],
        },
      ],
    },
    {
      path: 'questionnaires',
      name: CATALOG_ROUTES.QUESTIONNAIRES,
      component: () => lazyLoadView(import('@/contexts/administration/catalog/CatalogQuestionnairesView.vue')),
      meta: {
        i18nTitle: 'administration.menu.questionnaires',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: false,
        requires_role: piloteAndAuditor,
        doc_id: 'catalog',
        feature_id: 'CATALOG',
      },
      children: [
        {
          path: ':id',
          name: CATALOG_ROUTES.QUESTIONNAIRE,
          component: () => lazyLoadView(import('@/contexts/policies/PoliciesQuestionnaireView.vue')),
          meta: {
            title: null,
            is_authorized: (route: Route) => isAuthorized(route),
            requires_auth: true,
            requires_tenant: false,
            requires_role: piloteAndAuditor,
            doc_id: 'questionnaire',
          },
        },
      ],
    },
    {
      path: 'measures',
      name: CATALOG_ROUTES.MEASURES,
      component: () => lazyLoadView(import('@/contexts/administration/catalog/CatalogMeasuresView.vue')),
      meta: {
        i18nTitle: 'administration.menu.measures',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: false,
        requires_role: piloteAndAuditor,
        doc_id: 'catalog',
        feature_id: 'CATALOG',
      },
    },
    {
      path: 'actions',
      name: CATALOG_ROUTES.ACTIONS,
      component: () => lazyLoadView(import('@/contexts/administration/catalog/CatalogActionsView.vue')),
      meta: {
        i18nTitle: 'administration.menu.actions',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: true,
        requires_role: piloteAndAuditor,
        doc_id: 'catalog',
        feature_id: 'CATALOG',
      },
    },
    {
      path: 'metrics',
      name: CATALOG_ROUTES.METRICS,
      component: () => lazyLoadView(import('@/contexts/administration/catalog/CatalogMetricsView.vue')),
      meta: {
        i18nTitle: 'administration.menu.metrics',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: false,
        requires_role: piloteAndAuditor,
        doc_id: 'catalog',
        feature_id: 'CATALOG',
      },
    },
    {
      path: 'indicators',
      name: CATALOG_ROUTES.INDICATORS,
      component: () => lazyLoadView(import('@/contexts/administration/catalog/CatalogIndicatorsView.vue')),
      meta: {
        i18nTitle: 'administration.menu.indicators',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: false,
        requires_role: piloteAndAuditor,
        doc_id: 'catalog',
        feature_id: 'CATALOG',
      },
    },
    {
      path: 'runtasks',
      name: CATALOG_ROUTES.RECURRING_TASKS,
      component: () => lazyLoadView(import('@/contexts/administration/catalog/CatalogRecurringTasksView.vue')),
      meta: {
        i18nTitle: 'administration.menu.recurring_tasks',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: false,
        requires_role: piloteAndAuditor,
        doc_id: 'catalog',
        feature_id: 'CATALOG',
      },
    },
    {
      path: 'connectors',
      name: CATALOG_ROUTES.CONNECTORS,
      component: () => lazyLoadView(import('@/contexts/administration/catalog/CatalogConnectorsView.vue')),
      meta: {
        i18nTitle: 'administration.menu.connectors',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: false,
        requires_role: piloteAndAuditor,
        doc_id: 'catalog',
        feature_id: 'CATALOG',
      },
    },
    {
      path: 'issues',
      name: CATALOG_ROUTES.ISSUES,
      component: () => lazyLoadView(import('@/contexts/administration/catalog/CatalogIssuesView.vue')),
      meta: {
        i18nTitle: 'administration.menu.issues',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_no_tenant: true,
        requires_role: supportOnly,
        doc_id: 'catalog',
        feature_id: 'CATALOG',
      },
    },
  ],
}
